import { Edit } from '@mui/icons-material';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { EActiveDrawer, EInteractionMode, ESubMenu, EToolbarButton } from '../../../../@types/Enums';
import { useToolbarContext } from '../../../../provider/ToolbarProvider';
import RoomCreationModal from '../modals/RoomCreationModal';
import ToolbarButton from '../ToolbarButton';

interface RoomCreationButtonProps {
  sizes: any;
  handleToolbarButtonPress: (buttonId: EToolbarButton, subMenuType?: ESubMenu, activatedDrawer?: EActiveDrawer) => void;
  isDrawingAllowed: boolean;
  activeDrawer: EActiveDrawer;
  activeToolbarButton: EToolbarButton | null;
  setActiveToolbarButton: (button: EToolbarButton | null) => void;
  onPositionUpdate: (rect: DOMRect) => void;
}

const RoomCreationButton = forwardRef<HTMLButtonElement, RoomCreationButtonProps>(
  (
    { sizes, handleToolbarButtonPress, isDrawingAllowed, activeDrawer, activeToolbarButton, setActiveToolbarButton, onPositionUpdate },
    ref,
  ) => {
    const intl = useIntl();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const { interactionMode } =
    useToolbarContext();
    useImperativeHandle(ref, () => buttonRef.current!);

    useEffect(() => {
      if (activeToolbarButton !== EToolbarButton.ROOM_CREATION) {
        setModalOpen(false);
      }
    }, [activeToolbarButton]);

    // Update position if needed when active.
    useEffect(() => {
      if (activeToolbarButton === EToolbarButton.ROOM_CREATION && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        onPositionUpdate(rect);
      }
    }, [activeToolbarButton, onPositionUpdate]);

    const handleClick = () => {
      if (!modalOpen) {
        setModalOpen(true);
        if (activeToolbarButton !== EToolbarButton.ROOM_CREATION) {
          setActiveToolbarButton(EToolbarButton.ROOM_CREATION);
          handleToolbarButtonPress(EToolbarButton.ROOM_CREATION);
        }
      } else {
        setModalOpen(false);
      }
    };

    
    return (
      <>
        <ToolbarButton
          ref={buttonRef}
          tooltip={intl.formatMessage({ id: 'Toolbar.createRoom' })}
          onClick={handleClick}
          icon={<Edit />}
          // Check interactionMode === DRAW here
          active={
            modalOpen || 
            activeDrawer === EActiveDrawer.ARCHITECTURE || 
            interactionMode === EInteractionMode.ATTACHMENT || 
            interactionMode === EInteractionMode.DRAW
          }
          sizes={sizes}
        />

        <RoomCreationModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          sizes={sizes}
          handleToolbarButtonPress={handleToolbarButtonPress}
          isDrawingAllowed={isDrawingAllowed}
          activeDrawer={activeDrawer}
          interactionMode={interactionMode}
          setActiveToolbarButton={setActiveToolbarButton}
          buttonPosition={buttonRef.current?.getBoundingClientRect()}
        />
      </>
    );
  },
);

export default RoomCreationButton;
